import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api, ApiClientError } from '@/lib/api-client';
import { enrichmentKeys } from './enrichment';
import { publishingKeys } from './publishing';
import { tagSuggestionsKeys } from './tag-suggestions';
import type {
  RemoveImageResponse,
  SyncedLocation,
  PaginatedResponse,
  LocationStats,
  SyncLog,
  SyncedLocationListParams,
  SyncedLocationFacets,
  FacetGroup,
} from '@/types/api';

export const syncedLocationKeys = {
  all: ['synced-locations'] as const,
  list: (params: SyncedLocationListParams) => [...syncedLocationKeys.all, 'list', params] as const,
  facets: (params: SyncedLocationListParams, groups: readonly FacetGroup[]) =>
    [...syncedLocationKeys.all, 'facets', params, groups] as const,
  detail: (id: string) => [...syncedLocationKeys.all, 'detail', id] as const,
  stats: (partnerAccountId?: string) => [...syncedLocationKeys.all, 'stats', partnerAccountId] as const,
};

export const syncLogKeys = {
  all: ['sync-logs'] as const,
  byAccount: (accountId: string) => [...syncLogKeys.all, 'account', accountId] as const,
};

export type ListParams = SyncedLocationListParams;

/** The taxonomy lists may hold a comma inside a value, so they travel `||`-separated. */
const PIPE_SEPARATED = new Set(['category', 'tag', 'amenity']);

/**
 * The query string for a filter set. Only set values are sent, so the URL a
 * person shares says exactly what they filtered on and nothing else.
 */
export function buildListQuery(params: SyncedLocationListParams): string {
  const q = new URLSearchParams();
  const list = (key: string, values: readonly string[] | undefined): void => {
    if (values && values.length > 0) q.set(key, values.join(PIPE_SEPARATED.has(key) ? '||' : ','));
  };
  const scalar = (key: string, value: string | number | boolean | undefined): void => {
    if (value !== undefined && value !== '') q.set(key, String(value));
  };
  scalar('search', params.search);
  list('partnerAccountId', params.partnerAccountIds);
  list('source', params.sources);
  list('assetClass', params.assetClasses);
  list('country', params.countries);
  list('city', params.cities);
  scalar('enrichmentStatus', params.enrichmentStatus);
  scalar('publicationStatus', params.publicationStatus);
  scalar('isBlocked', params.isBlocked);
  list('locationTypeId', params.locationTypeIds);
  list('typeSource', params.typeSources);
  scalar('minTypeConfidence', params.minTypeConfidence);
  list('missing', params.missing);
  scalar('readyToPublish', params.readyToPublish);
  scalar('minImages', params.minImages);
  scalar('maxImages', params.maxImages);
  scalar('minCapacity', params.minCapacity);
  scalar('maxCapacity', params.maxCapacity);
  scalar('capacityUnknown', params.capacityUnknown);
  list('contact', params.contacts);
  scalar('bbox', params.bbox);
  scalar('near', params.near);
  scalar('radiusKm', params.radiusKm);
  scalar('titleSource', params.titleSource);
  scalar('aiContentFailed', params.aiContentFailed);
  scalar('createdFrom', params.createdFrom);
  scalar('createdTo', params.createdTo);
  scalar('updatedFrom', params.updatedFrom);
  scalar('updatedTo', params.updatedTo);
  scalar('syncedFrom', params.syncedFrom);
  scalar('syncedTo', params.syncedTo);
  scalar('inGroup', params.inGroup);
  scalar('duplicateName', params.duplicateName);
  scalar('addressMatchesPin', params.addressMatchesPin);
  scalar('hasPublished', params.hasPublished);
  scalar('hasOwner', params.hasOwner);
  scalar('hasPrice', params.hasPrice);
  list('category', params.categories);
  list('tag', params.tags);
  list('amenity', params.amenities);
  scalar('sortBy', params.sortBy);
  scalar('sortDir', params.sortDir);
  scalar('page', params.page);
  scalar('limit', params.limit);
  return q.toString();
}

export function useSyncedLocations(params: SyncedLocationListParams = {}) {
  const query = buildListQuery(params);

  return useQuery({
    queryKey: syncedLocationKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResponse<SyncedLocation>>(
        `/api/v1/synced-locations${query ? `?${query}` : ''}`
      ),
    staleTime: 0, // Always refetch to ensure fresh publication status
  });
}

/**
 * Counts per filter value within the current filter set, for the groups the
 * page shows. Facets are a dozen indexed counts on the server, so they are
 * cached for a minute and the page never blocks on them.
 */
export function useSyncedLocationFacets(
  params: SyncedLocationListParams,
  groups: readonly FacetGroup[],
  options: { enabled?: boolean } = {},
) {
  // The page and sort do not change the counts; leaving them out keeps the cache hit.
  const { page: _page, limit: _limit, sortBy: _sortBy, sortDir: _sortDir, ...filters } = params;
  const query = buildListQuery(filters);
  const facets = groups.join(',');

  return useQuery({
    queryKey: syncedLocationKeys.facets(filters, groups),
    queryFn: () =>
      api.get<SyncedLocationFacets>(
        `/api/v1/synced-locations/facets?${query ? `${query}&` : ''}facets=${facets}`
      ),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

/** How often the detail is refetched while AI work on the location is in flight. */
export const AI_WORK_POLL_MS = 2000;

/** How often it is refetched while the server cannot tell (its job lookup failed). */
export const AI_WORK_UNKNOWN_POLL_MS = 10_000;

/**
 * Whether the server reports AI work in flight on this location: an AI content job
 * (Regenerate or the first-sync auto-run) or a type classification. The work runs in a queue
 * worker, so its result reaches the page only by refetching.
 */
export function hasAiWorkInFlight(location: SyncedLocation | undefined): boolean {
  return location?.aiContent?.job != null || location?.isGeneratingType === true;
}

/**
 * The detail's refetch interval: fast while work is in flight; slower while the server could
 * not read the job (it may still be running, so polling must not stop, but the lookup is
 * failing); off otherwise.
 */
export function aiWorkPollInterval(location: SyncedLocation | undefined): number | false {
  if (hasAiWorkInFlight(location)) return AI_WORK_POLL_MS;
  if (location?.aiContent?.jobLookupFailed === true) return AI_WORK_UNKNOWN_POLL_MS;
  return false;
}

export function useSyncedLocation(id: string) {
  return useQuery({
    queryKey: syncedLocationKeys.detail(id),
    queryFn: () => api.get<SyncedLocation>(`/api/v1/synced-locations/${id}`),
    enabled: !!id,
    staleTime: 0, // Always refetch to ensure fresh location data
    // Poll while AI work is in flight, so its progress and result appear without a reload.
    // Stops by itself when the server reports the work finished (and while the tab is hidden).
    refetchInterval: (query) => aiWorkPollInterval(query.state.data),
  });
}

export function useLocationStats(partnerAccountId?: string) {
  const query = partnerAccountId ? `?partnerAccountId=${partnerAccountId}` : '';

  return useQuery({
    queryKey: syncedLocationKeys.stats(partnerAccountId),
    queryFn: () => api.get<LocationStats>(`/api/v1/synced-locations/stats${query}`),
  });
}

export function useSyncLogs(partnerAccountId: string, limit = 10) {
  return useQuery({
    queryKey: syncLogKeys.byAccount(partnerAccountId),
    queryFn: () =>
      api.get<SyncLog[]>(`/api/v1/partner-accounts/${partnerAccountId}/sync-logs?limit=${String(limit)}`),
    enabled: !!partnerAccountId,
  });
}

interface RegenerateAiContentResponse {
  jobId: string;
  syncedLocationId: string;
  status: 'enqueued';
  generation: number;
}

/**
 * Mutation for triggering AI content regeneration for a synced location (all partners).
 * Uses the POST /api/v1/synced-locations/:id/regenerate-ai-content endpoint, which only
 * enqueues the job. On success the detail is refetched — awaited, so the mutation stays
 * pending until the refetched detail carries the queued job and the panel's progress line
 * takes over without a gap; `useSyncedLocation` then polls until the job finishes.
 */
export function useRegenerateAiContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<RegenerateAiContentResponse>(
        `/api/v1/synced-locations/${id}/regenerate-ai-content`
      );
      return response;
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(id) });
    },
  });
}

export interface ClassifyLocationResponse {
  jobId: string;
  syncedLocationId: string;
  status: 'enqueued';
}

/** The `error` codes of the classify route's two 409 answers (R16). */
export type ClassifyConflictCode = 'ClassificationRunning' | 'NothingToClassify';

/**
 * The code of a 409 from the classify route, or null for any other error: `ClassificationRunning`
 * while a run for this location is in flight (D13), `NothingToClassify` when a person set the
 * category, styles and features, so a run could write nothing (R11). `apiRequest` puts the
 * body's `error` on `ApiClientError.errorCode` and its `message` on the error's message.
 */
export function classifyConflictCode(error: unknown): ClassifyConflictCode | null {
  if (!(error instanceof ApiClientError) || error.statusCode !== 409) {
    return null;
  }
  const code = error.errorCode;
  return code === 'ClassificationRunning' || code === 'NothingToClassify' ? code : null;
}

/** A 409 `ClassificationRunning` from the classify route: a run is already in flight (D13). */
export function isClassificationRunning(error: unknown): boolean {
  return classifyConflictCode(error) === 'ClassificationRunning';
}

/**
 * "Classify with AI" for one synced location (D13). POST /api/v1/synced-locations/:id/classify
 * only enqueues the run and sets the in-flight marker the detail reports as `isGeneratingType`.
 * Like Regenerate, the detail refetch is awaited, so the mutation stays pending until the
 * refetched detail shows the run and `useSyncedLocation` polls from there. The enrichment is
 * refetched too, because the route creates the enrichment row when the location had none.
 * On 409 `ClassificationRunning` a run (the button's or a batch's) is already in flight: the
 * detail is refetched so its generating state shows, and the caller tells the person instead of
 * reporting an error. On 409 `NothingToClassify` nothing runs and nothing is refetched; the
 * caller shows the server's message.
 */
export function useClassifyLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<ClassifyLocationResponse>(`/api/v1/synced-locations/${id}/classify`),
    onSuccess: async (_, id) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: enrichmentKeys.detail(id) }),
      ]);
    },
    onError: async (error, id) => {
      if (isClassificationRunning(error)) {
        await queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(id) });
      }
    },
  });
}

/**
 * Reloads the enrichment when a classification run the page saw in flight ends. A run writes the
 * category, subtypes, styles and features into the enrichment, which the form reads from
 * `useEnrichment`, not from the polled detail; Regenerate needs no such step because its result
 * is on the detail itself. Fires once per run, on `isGeneratingType` going from true to false,
 * whichever entry point started the run.
 */
export function useRefetchEnrichmentAfterClassification(
  syncedLocationId: string,
  isGeneratingType: boolean | undefined
): void {
  const queryClient = useQueryClient();
  const wasGenerating = useRef(isGeneratingType === true);

  useEffect(() => {
    const generating = isGeneratingType === true;
    const ended = wasGenerating.current && !generating;
    wasGenerating.current = generating;
    if (ended) {
      void queryClient.invalidateQueries({ queryKey: enrichmentKeys.detail(syncedLocationId) });
    }
  }, [isGeneratingType, syncedLocationId, queryClient]);
}

interface SetAiContentOverrideResponse {
  syncedLocationId: string;
  useAiContent: boolean;
}

/**
 * Mutation for toggling the AI content override for a synced location.
 * PUT /api/v1/synced-locations/:id/ai-content-override
 * On success, invalidates the location detail query to refresh the view.
 */
export function useSetAiContentOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, useAiContent }: { id: string; useAiContent: boolean }) => {
      const response = await api.put<SetAiContentOverrideResponse>(
        `/api/v1/synced-locations/${id}/ai-content-override`,
        { useAiContent }
      );
      return response;
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(id) });
    },
  });
}

/**
 * Remove one partner image from a file-import listing. The server rewrites the
 * gallery and remaps the cover and image tags, so every reader of the listing
 * is refetched: the detail, the enrichment (its coverImageIndex moved), the
 * tag suggestions (their image keys moved) and the publishing preview (its
 * validation warnings count the images).
 */
export function useRemoveLocationImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ syncedLocationId, url }: { syncedLocationId: string; url: string }) =>
      api.deleteWithBody<RemoveImageResponse>(`/api/v1/synced-locations/${syncedLocationId}/images`, { url }),
    // Awaited, so the mutation stays pending until the refetched gallery is in:
    // the page keeps every Remove disabled while it is pending, and a confirm
    // opened over the old, unshifted gallery would address the wrong photo.
    onSuccess: async (_, { syncedLocationId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: syncedLocationKeys.detail(syncedLocationId) }),
        queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all }),
        queryClient.invalidateQueries({ queryKey: enrichmentKeys.detail(syncedLocationId) }),
        queryClient.invalidateQueries({ queryKey: tagSuggestionsKeys.detail(syncedLocationId) }),
        queryClient.invalidateQueries({ queryKey: publishingKeys.preview(syncedLocationId) }),
      ]);
    },
  });
}
